<script setup lang="ts">
import type { ButtonProps } from '#layers/ui/components/Button.vue';
import type { FormFieldProps } from '#layers/ui/components/Form/FormField.vue';
import { computed } from 'vue';
import DButton from '#layers/ui/components/Button.vue';
import DFormField from '#layers/ui/components/Form/FormField.vue';
import DInput from '#layers/ui/components/Input.vue';
import DateTimePickerSetToNowButton from './SetToNowButton.vue';

interface Props extends Pick<ButtonProps, 'size' | 'disabled'> {
	label?: string;
	displayValue?: string;
	placeholder?: string;
	leadingIcon?: string;
	readonly?: boolean;
	canClear?: boolean;
	isSetToNowDisabled?: boolean;
	inputDataTestid?: string;
	clearDataTestid?: string;
	setToNowDataTestid?: string;
}

const props = withDefaults(defineProps<Props>(), {
	leadingIcon: 'lucide:calendar',
	inputDataTestid: 'picker-panel-input',
	clearDataTestid: 'picker-panel-clear',
	setToNowDataTestid: 'picker-panel-set-to-now',
});

const emit = defineEmits<{
	clear: [];
	setToNow: [];
}>();

// The label follows the picker size, clamped to the form field's supported range.
const formFieldSize = computed<FormFieldProps['size']>(() => {
	if (props.size === 'xs') return 'sm';
	if (props.size === 'xl') return 'lg';
	return props.size;
});
</script>

<template>
	<div class="flex w-fit flex-col gap-2">
		<DFormField :label :size="formFieldSize">
			<DInput
				:model-value="displayValue"
				color="neutral"
				:size
				:disabled
				:placeholder
				:leading-icon
				readonly
				:aria-readonly="readonly"
				class="w-full"
				:data-testid="inputDataTestid"
				:ui="{ base: 'ps-10', trailing: 'pe-1' }"
			>
				<template v-if="canClear && !readonly" #trailing>
					<DButton
						type="button"
						color="neutral"
						variant="link"
						size="sm"
						icon="lucide:x"
						:data-testid="clearDataTestid"
						:aria-label="$t('common.clear')"
						:disabled
						@click.stop.prevent="emit('clear')"
					/>
				</template>
			</DInput>
		</DFormField>

		<div class="w-fit overflow-hidden rounded-md border border-default">
			<div class="p-3">
				<slot />
			</div>
			<template v-if="!readonly">
				<DSeparator />
				<DateTimePickerSetToNowButton
					:size
					class="w-full justify-center rounded-t-none py-3"
					:data-testid="setToNowDataTestid"
					:disabled="disabled || isSetToNowDisabled"
					@click="emit('setToNow')"
				/>
			</template>
		</div>
	</div>
</template>
